import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { buildData } from '../../data/mock';

const BuildSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [terminalLines, setTerminalLines] = useState([]);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const text = buildData.terminalOutput.join('\n');
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e) { /* noop */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible && terminalLines.length < buildData.terminalOutput.length) {
      const timer = setTimeout(() => {
        setTerminalLines((prev) => [
          ...prev,
          buildData.terminalOutput[prev.length]
        ]);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible, terminalLines]);

  return (
    <section className="fullscreen-section" id="build" ref={sectionRef} data-testid="build-section">
      <div className="section-inner">
        <div className={`section-visual ${isVisible ? 'animate-fade-left' : ''}`}>
          <div className="terminal-card">
            <div className="terminal-header">
              <div className="terminal-dots">
                <span className="dot red"></span>
                <span className="dot yellow"></span>
                <span className="dot green"></span>
              </div>
              <span className="terminal-title">zyber@secure: ~/build</span>
              <button
                className="terminal-copy"
                onClick={handleCopy}
                aria-label="Copy commands"
                data-testid="terminal-copy-btn"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                <span>{copied ? 'Copied' : 'Copy'}</span>
              </button>
            </div>
            <div className="terminal-body" data-testid="build-terminal-body">
              {terminalLines.map((line, index) => (
                <div key={index} className="terminal-line">
                  <span className={line.startsWith('$') ? 'command' : 'output'}>
                    {line}
                  </span>
                </div>
              ))}
              <div className="terminal-cursor"></div>
            </div>
          </div>
        </div>

        <div className={`section-content ${isVisible ? 'animate-fade-right delay-2' : ''}`}>
          <span className="section-number">05</span>
          <p className="section-label-small">For Developers</p>
          <h2 className="section-title">Zyber Build</h2>
          <p className="section-description">
            Secure AI-assisted development. Zero local exposure. Build full-stack 
            products with no dependency risk. Real-time cost simulation.
          </p>
          <a href="#" className="btn-solid" data-testid="build-launch-btn" onClick={(e) => { e.preventDefault(); toast('Coming soon', { description: 'The full app is launching soon. Explore the live demo in the Solution section.' }); document.getElementById('solution')?.scrollIntoView({ behavior: 'smooth' }); }}>
            Launch App
            <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default BuildSection;
